import React from 'react'
import { useIntl } from 'react-intl'
import {
  ActionCenterInsightSeverity,
  ActionCenterInsightType,
  IActionCenterInsight,
  IActionCenterInsightHydratedUserEntry,
  MemberStatus,
} from '@vacationtracker/shared/types/team-energy'
import { STATUS_I18N_KEY } from './StatusPill'

interface IActionCenterCardProps {
  insight: IActionCenterInsight
}

const SEVERITY_ICON: Record<ActionCenterInsightSeverity, string> = {
  CRITICAL: '⛈️',
  WARNING: '⚠️',
  INFO: '✨',
  POSITIVE: '💚',
}

const RULE_TYPE_I18N: Record<ActionCenterInsightType, { titleKey: string; descriptionKey: string }> = {
  DRAINED_DECLINING_TREND: {
    titleKey: 'teamEnergy.action.drainedDeclining.title',
    descriptionKey: 'teamEnergy.action.drainedDeclining.description',
  },
  OVERDUE_FOR_PTO: {
    titleKey: 'teamEnergy.action.overdueForPto.title',
    descriptionKey: 'teamEnergy.action.overdueForPto.description',
  },
  BRIDGE_OPPORTUNITY: {
    titleKey: 'teamEnergy.action.bridgeOpportunity.title',
    descriptionKey: 'teamEnergy.action.bridgeOpportunity.description',
  },
  IMPROVED_THIS_WEEK: {
    titleKey: 'teamEnergy.action.improvedThisWeek.title',
    descriptionKey: 'teamEnergy.action.improvedThisWeek.description',
  },
}

function joinWithConjunction(parts: string[], and: string): string {
  if (parts.length === 0) {
    return ''
  }
  if (parts.length === 1) {
    return parts[0]
  }
  if (parts.length === 2) {
    return `${parts[0]} ${and} ${parts[1]}`
  }
  return `${parts.slice(0, -1).join(', ')}, ${and} ${parts[parts.length - 1]}`
}

const ActionCenterCard: React.FC<IActionCenterCardProps> = ({ insight }) => {
  const { formatMessage } = useIntl()
  const { titleKey, descriptionKey } = RULE_TYPE_I18N[insight.type]
  const and = formatMessage({ id: 'app.and' })

  const names = joinWithConjunction(insight.userNames, and)

  // Build a "userList" - "Name (Nd)" entries, joined - for OVERDUE_FOR_PTO
  const userEntriesEntry = insight.descriptionVars.userEntries
  const userEntries: IActionCenterInsightHydratedUserEntry[] = Array.isArray(userEntriesEntry) ? userEntriesEntry : []
  const userList = joinWithConjunction(
    userEntries.map((e) => (typeof e.days === 'number' ? `${e.userName} (${e.days}d)` : e.userName)),
    and
  )

  // Resolve count for the simpler single-day variable.
  const firstDays = userEntries.length > 0 && typeof userEntries[0].days === 'number' ? userEntries[0].days : 0

  // Build the variables for ICU formatting. Mix descriptionVars + computed names/userList.
  const titleVars: Record<string, string | number> = {
    ...insight.titleVars,
    names,
  }

  // mode arrives as a raw EnergyMode value - render it as the translated status label
  const modeKey = typeof titleVars.mode === 'string' ? STATUS_I18N_KEY[titleVars.mode as MemberStatus] : undefined
  if (modeKey) {
    titleVars.mode = formatMessage({ id: modeKey })
  }

  const descriptionVars: Record<string, string | number> = {
    names,
    userList,
    days: firstDays,
    count: insight.userIds.length,
  }
  for (const [k, v] of Object.entries(insight.descriptionVars)) {
    if (typeof v === 'string' || typeof v === 'number') descriptionVars[k] = v
  }

  // previousMode arrives as a raw EnergyMode value - render it as the translated status
  // label. Insights stored before the rule emitted previousMode get an empty value so
  // ICU formatting still succeeds ("Up from last week." instead of the raw pattern).
  const previousModeKey = typeof descriptionVars.previousMode === 'string' ? STATUS_I18N_KEY[descriptionVars.previousMode as MemberStatus] : undefined
  descriptionVars.previousMode = previousModeKey ? formatMessage({ id: previousModeKey }) : ''

  return (
    <div className={`te-action-card te-action-card--${insight.severity.toLowerCase()}`}>
      <span className="te-action-card__icon">{SEVERITY_ICON[insight.severity]}</span>
      <div className="te-action-card__body">
        <div className="te-action-card__title-row">
          <span className="te-action-card__title">{formatMessage({ id: titleKey }, titleVars)}</span>
          {insight.isAiGenerated && <span className="te-action-card__ai-badge">{formatMessage({ id: 'teamEnergy.memberDetail.aiPowered' })}</span>}
        </div>
        <div className="te-action-card__description">{formatMessage({ id: descriptionKey }, descriptionVars)}</div>
      </div>
    </div>
  )
}

export default ActionCenterCard
